import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UserPlus,
  CalendarCheck,
  Stethoscope,
  ArrowRight,
  Sparkles,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { publicService } from '@/services/publicService';
import type { PublicCampaign } from '@/types/public';
import styles from './HomePage.module.css';

/**
 * Public landing page for patients.
 *
 * Sections:
 * 1. Header with login/register links
 * 2. Hero with CTA to create patient account
 * 3. Active campaigns with packages
 * 4. "Como funciona" steps
 * 5. Footer
 */
export function HomePage() {
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['public-campaigns'],
    queryFn: () => publicService.getActiveCampaigns(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <nav className={styles.nav} aria-label="Navegação principal">
          <Link to="/" className={styles.logo}>
            CRM<span className={styles.logoAccent}>Clinica</span>
          </Link>
          <div className={styles.navLinks}>
            <Link to="/login" className={styles.navLink}>
              Entrar
            </Link>
            <Link to="/portal/register" className={styles.registerLink}>
              <Button variant="primary" size="sm">
                Criar conta
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroContent}>
            <span className={styles.heroBadge}>
              <CalendarCheck size={14} aria-hidden="true" />
              Agendamento Online
            </span>
            <h1 id="hero-title" className={styles.heroTitle}>
              Cuide da sua saúde{' '}
              <span className={styles.heroHighlight}>com quem entende</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Agende sua consulta de avaliação online. Escolha o profissional,
              a data e o horário — tudo em poucos cliques.
            </p>
            <div className={styles.heroActions}>
              <Link to="/portal/register">
                <Button variant="primary" size="lg" leftIcon={<UserPlus size={20} />}>
                  Criar minha conta
                </Button>
              </Link>
              <a href="#campanhas">
                <Button variant="outline" size="lg">
                  Ver campanhas
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Active Campaigns */}
        <section
          className={styles.campaigns}
          id="campanhas"
          aria-labelledby="campaigns-title"
        >
          <h2 id="campaigns-title" className={styles.sectionTitle}>
            <Sparkles size={24} aria-hidden="true" className={styles.sectionIcon} />
            Campanhas Ativas
          </h2>
          <p className={styles.sectionSubtitle}>
            Confira as campanhas disponíveis e agende sua avaliação.
          </p>

          {isLoading ? (
            <div className={styles.loadingState} role="status" aria-busy="true">
              <p>Carregando campanhas...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={styles.emptyState} role="status">
              <p>Nenhuma campanha ativa no momento.</p>
              <p className={styles.emptyHint}>
                Cadastre-se para ser notificado quando novas campanhas estiverem disponíveis.
              </p>
            </div>
          ) : (
            <div className={styles.campaignGrid}>
              {campaigns.map((campaign) => (
                <CampaignCard key={campaign.slug} campaign={campaign} />
              ))}
            </div>
          )}
        </section>

        {/* How it works */}
        <section className={styles.steps} aria-labelledby="steps-title">
          <h2 id="steps-title" className={styles.sectionTitle}>
            Como funciona
          </h2>
          <div className={styles.stepsGrid}>
            <StepCard
              number={1}
              icon={<UserPlus size={28} aria-hidden="true" />}
              title="Crie sua conta"
              description="Cadastre-se gratuitamente com seus dados básicos em menos de 2 minutos."
            />
            <StepCard
              number={2}
              icon={<Stethoscope size={28} aria-hidden="true" />}
              title="Escolha sua campanha"
              description="Veja as campanhas disponíveis e selecione o pacote de procedimentos ideal para você."
            />
            <StepCard
              number={3}
              icon={<CalendarCheck size={28} aria-hidden="true" />}
              title="Agende seus procedimentos"
              description="Escolha o profissional, a data e o horário que melhor se encaixam na sua rotina."
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Pronto para cuidar da sua saúde?</h2>
            <p className={styles.ctaSubtitle}>
              Crie sua conta e agende sua primeira consulta de avaliação.
            </p>
            <Link to="/portal/register">
              <Button variant="primary" size="lg" leftIcon={<UserPlus size={20} />}>
                Começar agora
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <p>&copy; 2026 CRMClinica. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
}

/* ── Subcomponents ─────────────────────────────────────── */

interface CampaignCardProps {
  campaign: PublicCampaign;
}

function CampaignCard({ campaign }: CampaignCardProps) {
  const minPrice = campaign.packages.length > 0
    ? Math.min(...campaign.packages.map((p) => p.price))
    : null;

  return (
    <article className={styles.campaignCard}>
      <div className={styles.campaignHeader}>
        <h3 className={styles.campaignName}>{campaign.name}</h3>
        {campaign.description && (
          <p className={styles.campaignDescription}>{campaign.description}</p>
        )}
      </div>

      {campaign.packages.length > 0 && (
        <div className={styles.packageList}>
          {campaign.packages.map((pkg) => (
            <div key={pkg.id} className={styles.packageItem}>
              <div className={styles.packageInfo}>
                <Package size={16} aria-hidden="true" />
                <span className={styles.packageName}>{pkg.name}</span>
              </div>
              <span className={styles.packagePrice}>
                {pkg.price.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.campaignFooter}>
        {minPrice !== null && (
          <span className={styles.priceFrom}>
            A partir de{' '}
            <strong>
              {minPrice.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </strong>
          </span>
        )}
        <Link
          to={`/widget/campaign/${campaign.slug}`}
          className={styles.campaignCtaButton}
        >
          Tenho interesse <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

interface StepCardProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function StepCard({ number, icon, title, description }: StepCardProps) {
  return (
    <article className={styles.stepCard}>
      <div className={styles.stepNumber} aria-hidden="true">{number}</div>
      <div className={styles.stepIcon}>{icon}</div>
      <h3 className={styles.stepTitle}>{title}</h3>
      <p className={styles.stepDescription}>{description}</p>
    </article>
  );
}
